/**
 * Loading one distinct `objectRef` into the geometry every placement of it
 * shares — the half of `world-instancer.ts` that is about *a model*, split out
 * of it because the host reached the 400-line ceiling.
 *
 * One `SharedRef` per ref, built once however many times the region places it:
 * the chosen LOD's triangles as a rotated `BufferGeometry`, its own line faces
 * and its triangles' outlines merged into one segment source
 * (`polygon-outlines.ts` for why the outlines exist at all), and the three
 * non-triangle node kinds a `.TBL` LOD can carry. The hour's dimming is applied
 * here rather than downstream, because it has to land **before** the outline
 * segments are read off the geometry or a road would keep its noon colour over
 * a dusk field.
 */

import * as THREE from "three/webgpu";
import {
  bestDrawableLod,
  createLodGeometry,
  loadBakedModel,
  type BakedModelBillboard,
  type BakedModelBody,
  type BakedModelSphere,
  type BakedModelSpriteSheet,
} from "./baked-model.ts";
import {
  boundaryOutlineSegments,
  concatSegments,
  lineFaceSegments,
  triangleOutlineSegments,
  type SegmentSource,
} from "./polygon-outlines.ts";
import { placementWorldTransform, type PlacementOrigin } from "../world/placement-transform.ts";
import { SEA_PLATE_DEPTH_M, SEA_SCALE_EXPONENT } from "../world/water-mask.ts";
import { colourToRgb, dimTerrainColour } from "../world/terrain-palette.ts";
import { loadEnhancedStructure } from "./enhanced-structure.ts";
import { STATIC_STATE_INDEX } from "./sphere-instances.ts";
import type { GroundSampler } from "./terrain-drape.ts";

/** One placed instance of a baked object, as a region's `.WLD` records it. */
export interface InstancerPlacement {
  readonly objectRef: string | null;
  readonly x: number;
  readonly y: number;
  readonly rotationDegrees: number;
}

/** The model-frame -90° X convention (`world/placement-transform.ts`), as a `Matrix4` rotation for `geometry.rotateX`. */
const MODEL_TO_WORLD_PITCH_RAD = -Math.PI / 2;

/** The chosen LOD's non-triangle nodes, filtered to the state a static instance draws. */
export interface SharedPrimitives {
  /** Sphere nodes — the tree canopies. */
  readonly spheres: readonly BakedModelSphere[];
  /** Sprite nodes, meaningful only alongside `spriteSheet`. */
  readonly billboards: readonly BakedModelBillboard[];
  readonly spriteSheet: BakedModelSpriteSheet | undefined;
}

export interface SharedRef extends SharedPrimitives {
  readonly geometry: THREE.BufferGeometry;
  readonly segments: SegmentSource;
  readonly boundingRadius: number;
  /**
   * Whether this ref is one of the two `s = 10` sea plates — the same
   * `scaleExponent >= SEA_SCALE_EXPONENT` test the draught below is applied on,
   * reported so `world-instancer.ts` can hand it ENHANCED TERRAIN's ocean
   * material without re-reading the model body.
   *
   * A property of the *object*, not of its faces: Dunkirk's `TERR2-51` carries
   * nine land polygons of the far English shore alongside its forty water ones,
   * and which of those the shader treats as sea is `three/ocean-material.ts`'s
   * own per-fragment `anchorIndex` gate. This flag only says "there is open sea
   * in this model".
   */
  readonly isSeaPlate: boolean;
  /**
   * The ENHANCED MODELS geometry for this object, when the manifest has one and
   * the switch asked for it (`three/enhanced-structure.ts`) — **pending**, and
   * that is the whole point of this field's type.
   *
   * It used to be the geometry itself, awaited inside `loadSharedRef`, and that
   * put the whole region behind it: `world-instancer.ts` gathers every ref with
   * one `Promise.all`, so a single slow or stalled `.glb` held back *all* of a
   * region's baked scenery — every plate, road, river, hill and building —
   * behind an optional refinement to a few dozen of them. On a LAN that is
   * invisible; on a thin link it is a world that does not appear. The comment
   * on the fetch claimed it "costs this ref nothing but its own request", which
   * was true of the request and false of the wait.
   *
   * So the bake draws the moment its placements are known and each enhanced
   * geometry swaps itself in whenever it lands (`structureSwap`). Never
   * rejects: `loadEnhancedStructure` answers `undefined` for anything it could
   * not read, which is the "a mixed world is the designed case" rule.
   *
   * Held *beside* the baked geometry rather than in place of it because the
   * setting is a toggle a player flips mid-sortie, and rebuilding a whole
   * region's instancer to answer it would cost a visible stall. Both live on
   * the same `InstancedMesh` slot and `setEnhancedStructures` swaps which one is
   * drawn, which is the arrangement `setLandcoverShading` already has for the
   * plates' two colour buffers.
   *
   * The **outline segments are the baked mesh's either way**. They are one
   * original pixel of terrain edge (`polygon-outlines.ts`), and a building with
   * 900 to 2 400 triangles instead of 40 would contribute thousands of them per
   * placement to a buffer that is merged on the CPU — for edges nobody asked to
   * change. The enhanced switch is about the *surfaces*.
   */
  readonly enhancedGeometry?: Promise<THREE.BufferGeometry | undefined>;
}

const EMPTY_SEGMENTS: SegmentSource = { positions: new Float32Array(0), colors: new Float32Array(0), segmentCount: 0 };

/** A ref whose chosen LOD has no triangles still draws if it has lines, spheres or sprites; if it has none of the four it is nothing. */
function nonMeshRef(
  segments: SegmentSource | undefined,
  primitives: SharedPrimitives,
  boundingRadius: number,
): SharedRef | undefined {
  if (!segments && primitives.spheres.length === 0 && primitives.billboards.length === 0) return undefined;
  return {
    geometry: new THREE.BufferGeometry(),
    segments: segments ?? EMPTY_SEGMENTS,
    boundingRadius,
    isSeaPlate: false,
    ...primitives,
  };
}

/**
 * Walks every vertex colour of a placed terrain mesh down its own run for the
 * hour. Done before the outline segments are read off the geometry, so the
 * one-pixel edges dim with the faces they belong to.
 */
function dimGeometryColours(geometry: THREE.BufferGeometry, shift: number): void {
  const attribute = geometry.getAttribute("color");
  if (!attribute) return;
  const array = attribute.array as Float32Array;
  for (let i = 0; i < array.length; i += 3) {
    const packed =
      (Math.round(array[i]! * 255) << 16) | (Math.round(array[i + 1]! * 255) << 8) | Math.round(array[i + 2]! * 255);
    const [r, g, b] = colourToRgb(dimTerrainColour(packed, shift));
    array[i] = r;
    array[i + 1] = g;
    array[i + 2] = b;
  }
  attribute.needsUpdate = true;
}

export async function loadSharedRef(
  objectRef: string,
  preloaded: BakedModelBody | undefined,
  rampShift: number,
  enhancedPath?: string,
): Promise<SharedRef | undefined> {
  const body = preloaded ?? (await loadBakedModel(`${objectRef}.json`));
  const lod = bestDrawableLod(body.lods);
  const baked = lineFaceSegments(lod?.lines);
  const primitives: SharedPrimitives = {
    spheres: (lod?.spheres ?? []).filter((sphere) => sphere.stateIndex === STATIC_STATE_INDEX),
    billboards: (lod?.billboards ?? []).filter((billboard) => billboard.stateIndex === STATIC_STATE_INDEX),
    spriteSheet: body.spriteSheet,
  };
  if (!lod || lod.triangleCount === 0) {
    // Objects made entirely of 2-point faces (40 of the corpus's `TERRn`
    // slots are roads and rivers with no triangle at any LOD) — or entirely of
    // sprites — still have something to draw.
    return nonMeshRef(baked, primitives, body.boundingRadius);
  }
  const geometry = createLodGeometry(lod);
  geometry.rotateX(MODEL_TO_WORLD_PITCH_RAD);
  const isSeaPlate = (body.scaleExponent ?? 0) >= SEA_SCALE_EXPONENT;
  // The sea gets a metre of draught, so water is unambiguously *under* land.
  // `london-sea-placements.md` §1.4: an `s = 10` plate is a flat quad at
  // `y = 0`, exactly coplanar with every town's ground pad, whose own `y` is
  // also 0 — and `three/decal-layers.ts`'s 4 mm nudge is per *model*, so it
  // does nothing across two of them. The two z-fight. Done here rather than in
  // the instance matrix so the outline segments read off this geometry a few
  // lines down sink with it. See `world/water-mask.ts` for why a metre.
  if (isSeaPlate) geometry.translate(0, -SEA_PLATE_DEPTH_M, 0);

  if (rampShift !== 0) dimGeometryColours(geometry, rampShift);

  const surface = {
    positions: geometry.getAttribute("position").array,
    colors: geometry.getAttribute("color").array,
  };
  // A sea plate keeps only its **boundary** edges: its fan's spokes are
  // triangulation rather than geography, and once ENHANCED TERRAIN shades the
  // water around them they read as dark rays converging on the pivot vertex
  // (`polygon-outlines.ts`'s `boundaryOutlineSegments` has the capture that
  // found it). The coastline itself is a one-off edge and survives.
  const outlines = isSeaPlate ? boundaryOutlineSegments(surface) : triangleOutlineSegments(surface);
  // Started, **not** awaited: the bake this ref already has is what the region
  // draws, and the refinement catches up. See `SharedRef.enhancedGeometry`.
  const enhancedGeometry = enhancedPath ? loadEnhancedStructure(enhancedPath, rampShift) : undefined;
  return {
    geometry,
    segments: baked ? concatSegments([outlines, baked]) : outlines,
    boundingRadius: body.boundingRadius,
    isSeaPlate,
    ...(enhancedGeometry ? { enhancedGeometry } : {}),
    ...primitives,
  };
}

export function instanceMatrixFor(
  placement: InstancerPlacement,
  origin: PlacementOrigin,
  ground: GroundSampler | undefined,
): THREE.Matrix4 {
  const transform = placementWorldTransform(placement, origin);
  const matrix = new THREE.Matrix4();
  matrix.makeRotationY(transform.yawRadians);
  // A small object is dropped bodily onto the surface under its own origin —
  // see `terrain-drape.ts` for why per-vertex draping is wrong for these.
  const y = transform.position.y + (ground ? ground(transform.position.x, transform.position.z) : 0);
  matrix.setPosition(transform.position.x, y, transform.position.z);
  return matrix;
}
